import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTenant } from '../../auth/TenantContext';
import { LoadingState, ErrorState } from '../ui/States';
import { CreateWorkspaceForm } from '../workspace/CreateWorkspaceForm';
import { BRAND_ROUTE_ERROR_TEXT } from '../../features/brand/BrandRoute';
import { setupPath } from '../../features/brand/paths';
import { useBrandWorkspaceV2, type BrandWorkspaceV2State } from '../../features/brand/useBrandFlags';
import { CreateBrandWorkspaceForm } from '../../features/brand-setup/create/CreateBrandWorkspaceForm';
import { ComposerModal } from '../../features/composer/ComposerModal';
import { GenerateMediaModalHost } from '../../features/media/GenerateMediaModalHost';
import { GenerateDesignModalHost } from '../../features/design/GenerateDesignModalHost';
import { BottomNav } from './BottomNav';
import { CreateContentButton } from './CreateContentButton';
import { NavRail } from './NavRail';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { AccessSuspendedLock } from './AccessSuspendedLock';
import { isAccessEscapeRoute, isWorkspaceAgnosticRoute } from './accessRoutes';
import { useAccessLock } from './useAccessLock';
import { useNavBadges } from './useNavBadges';

/**
 * Gates the app on the user having at least one Brand Workspace.
 *
 * - While workspaces load → a loading state inside the shell chrome.
 * - No workspace (brand-new user) → the shell chrome with a disabled sidebar
 *   and a non-dismissable "Create your first Brand Workspace" screen in the
 *   content area.
 * - Otherwise → the routed page content as normal.
 *
 * The frame: the full-width top bar, then a row holding the navigation and the content, and on a phone
 * the bottom nav under that. The navigation takes one shape per width, all three rendered and CSS
 * showing one: the sidebar from `lg` up, the icon rail from `md`, the bottom nav below `md`. The
 * height stays bounded (the dynamic viewport height, so a phone's browser toolbars never hide the
 * bottom nav, where the browser has one, else the viewport height; then min-h-0 down the chain), so
 * each page owns its own scroll region.
 */
export function WorkspaceGate() {
  const { hasWorkspace, isLoading, error, refresh } = useTenant();
  const { pathname } = useLocation();
  const access = useAccessLock();
  const badges = useNavBadges();
  // The install's Brand Workspace v2 flag, read here once for the whole session (web.md §2.1): the
  // first-login screen needs it, and the Brand Workspace pages and the test-mode link then find it
  // already read instead of waiting on it. A failed read shows only where the flag decides something:
  // the first-login screen and those pages, each with Try again.
  const brandFlag = useBrandWorkspaceV2();

  // The billing pages, and every other install-level or per-user page, render WITHOUT a Brand
  // Workspace. Someone who belongs to no workspace is a normal state here, not a half-finished
  // signup: a site admin configuring an install on a customer's behalf has no reason to own a Brand
  // Workspace there, because Brand Workspaces are the customer's.
  //
  // Gating every route on one did two kinds of damage. It landed the payment lock's own "Go to
  // Billing" on "Create your first Brand Workspace", whose only action is a write the lock refuses
  // — a customer with no way to pay out of it. And it locked site admins out of /app-settings, the
  // page where an install's user directory is configured, before any workspace could exist.
  const locked =
    !hasWorkspace && !isAccessEscapeRoute(pathname) && !isWorkspaceAgnosticRoute(pathname);

  return (
    <div className="flex h-screen overflow-hidden supports-[height:100dvh]:h-dvh">
      {/* Blurred AND inert while the app is withheld: a blur alone still lets someone tab into
          the page behind it and keep working. */}
      <div
        className={
          access.locked
            ? 'pointer-events-none flex h-full w-full select-none flex-col overflow-hidden blur-sm'
            : 'flex h-full w-full flex-col overflow-hidden'
        }
        {...(access.locked ? { inert: '', 'aria-hidden': true } : {})}
      >
      <Topbar />
      <div className="flex min-h-0 flex-1">
        <Sidebar disabled={locked} create={<CreateContentButton placement="sidebar" />} badges={badges} />
        <NavRail disabled={locked} create={<CreateContentButton placement="rail" />} badges={badges} />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <LoadingState label="Loading your workspaces…" />
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center p-4">
              <ErrorState
                title="Couldn’t load your workspaces"
                message={error}
                onRetry={() => void refresh()}
              />
            </div>
          ) : locked ? (
            <FirstWorkspaceScreen brandFlag={brandFlag} />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
      <BottomNav disabled={locked} create={<CreateContentButton placement="bar" />} badges={badges} />
      {/* Site-wide Content Composer modal — driven by the ?compose= search param,
          so "New content" / Edit can open it over any page. */}
      {!locked && <ComposerModal />}
      {!locked && <GenerateMediaModalHost />}
      {!locked && <GenerateDesignModalHost />}
      </div>
      {access.locked && <AccessSuspendedLock note={access.note} />}
    </div>
  );
}

/** Non-dismissable first-run screen: the user must create a workspace to proceed. */
function FirstWorkspaceScreen({ brandFlag }: { brandFlag: BrandWorkspaceV2State }) {
  return (
    <div className="h-full overflow-y-auto bg-page px-4 py-10">
      <div className="mx-auto w-full max-w-xl">
        <div className="rounded-2xl border border-line bg-card p-8 shadow-panel">
          <h1 className="text-xl font-semibold text-ink">Create your first Brand Workspace</h1>
          <p className="mt-1 text-sm text-ink-muted">
            A Brand Workspace is where a brand's content, sources, and digests live. Create one to
            start using Smorgas Social — you can add more later and switch between them any time.
          </p>
          <div className="mt-6">
            <FirstWorkspaceForm brandFlag={brandFlag} />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The first workspace's form, by the install's Brand Workspace v2 flag (web.md §2.5, E35):
 * - off: today's form;
 * - on: the name and the website, then Brand Setup for the new workspace. `replace`, so Back cannot
 *   return to a form that has done its job. Creating it fills the workspace list, so this screen gives
 *   way to the page underneath before the wizard opens;
 * - not known yet: a loading state;
 * - the read failed: an error with Try again. It never guesses: today's form writes brand text that is
 *   read-only while the flag is on, and the v2 form leaves out what today's requires while it is off.
 */
function FirstWorkspaceForm({ brandFlag }: { brandFlag: BrandWorkspaceV2State }) {
  const navigate = useNavigate();
  // No onCancel on either form: this screen is non-dismissable until a workspace exists.
  if (brandFlag.enabled === false) {
    return <CreateWorkspaceForm onCreated={() => undefined} submitLabel="Create Brand Workspace" />;
  }
  if (brandFlag.enabled === true) {
    return (
      <CreateBrandWorkspaceForm
        submitLabel="Create Brand Workspace"
        onCreated={(created) => navigate(setupPath(created.slug), { replace: true })}
      />
    );
  }
  if (brandFlag.isError) return <ErrorState message={BRAND_ROUTE_ERROR_TEXT} onRetry={brandFlag.refetch} />;
  return <LoadingState />;
}
